import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { Navbar } from './components/Navbar';
import { Dashboard } from './components/Dashboard';
import { VulnerabilityAnalysis } from './pages/VulnerabilityAnalysis';
import { SettingsPage } from './pages/SettingsPage';
import { ScansPage } from './pages/ScansPage';
import { BulletinsPage } from './pages/BulletinsPage';
import { TeamsPage } from './pages/TeamsPage';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { ClientVulnerabilityPage } from './pages/ClientVulnerabilityPage';
import { ReportPage } from './pages/ReportPage';
import { StartScanModal } from './components/StartScanModal';
import { ConfigureScansModal } from './components/ConfigureScansModal';
import { AddAccountModal } from './components/AddAccountModal';
import { AddClientModal } from './components/AddClientModal';
import { ManageEnvironmentModal } from './components/ManageEnvironmentModal';
import { ReportConfigModal } from './components/ReportConfigModal';
import { WelcomeScreen } from './components/WelcomeScreen';
import type { Environment } from './components/RecentScans';
export interface BrandingConfig {
  brandName: string;
  primaryColor: string;
  reportFooter: string;
  removePoweredBy: boolean;
  includeCoverPage: boolean;
  customDomain: string;
  emailSenderName: string;
  logoUrl: string;
}
export function App() {
  const [authState, setAuthState] = useState<'logged-out' | 'logging-in' | 'registering' | 'checkout' | 'logged-in'>('logged-out');
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [orgName, setOrgName] = useState('My Organization');
  const [orgStatus, setOrgStatus] = useState('trial');
  const [branding, setBranding] = useState<BrandingConfig>({
    brandName: 'Astrolayb',
    primaryColor: '#22c55e',
    reportFooter: 'Confidential — Generated by Astrolayb Security Intelligence Platform',
    removePoweredBy: false,
    includeCoverPage: true,
    customDomain: '',
    emailSenderName: '',
    logoUrl: ''
  });
  const [activePage, setActivePage] = useState<'dashboard' | 'vulnerability' | 'settings' | 'scans' | 'bulletins' | 'teams' | 'client-vulnerability' | 'report'>('dashboard');
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [isConfigureOpen, setIsConfigureOpen] = useState(false);
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [managedEnv, setManagedEnv] = useState<Environment | null>(null);
  const [selectedClientEnv, setSelectedClientEnv] = useState<Environment | null>(null);
  const [reportSections, setReportSections] = useState<string[]>([]);
  const [reportConfigEnv, setReportConfigEnv] = useState<Environment | null>(null);
  const [previousPage, setPreviousPage] = useState<string>('dashboard');
  const navigateTo = (page: typeof activePage) => {
    setActivePage(page);
    window.scrollTo(0, 0);
  };
  // Track page views on navigation and auth state changes
  useEffect(() => {
    const pagePath = authState === 'logged-in' ? `/${activePage}` : `/${authState}`;
    ReactGA.send({
      hitType: 'pageview',
      page: pagePath,
      title: pagePath
    });
  }, [activePage, authState]);
  if (authState === 'logged-out') {
    return <LandingPage onLogin={() => setAuthState('logging-in')} onRegister={() => setAuthState('registering')} data-id="element-0" />;
  }
  if (authState === 'logging-in') {
    return <LoginPage onLogin={() => {
      setAuthState('logged-in');
      setIsFirstLogin(false);
    }} onGoToRegister={() => setAuthState('registering')} data-id="element-1" />;
  }
  if (authState === 'registering') {
    return <RegisterPage onRegister={() => {
      setAuthState('checkout');
    }} onBackToLogin={() => setAuthState('logging-in')} data-id="element-2" />;
  }
  if (authState === 'checkout') {
    return <CheckoutPage onComplete={(newOrgName, newClients) => {
      setOrgName(newOrgName);
      setAuthState('logged-in');
      setIsFirstLogin(true);
      setOrgStatus('active');
    }} onBack={() => setAuthState('registering')} data-id="element-3" />;
  }
  const handleViewClientVulns = (env: Environment) => {
    setPreviousPage(activePage);
    setSelectedClientEnv(env);
    navigateTo('client-vulnerability');
  };
  const handleViewReport = (env: Environment, sections: string[]) => {
    setSelectedClientEnv(env);
    setReportSections(sections);
    navigateTo('report');
  };
  const handleOpenReportConfig = (env: Environment) => {
    setReportConfigEnv(env);
  };
  return <div className="min-h-screen bg-astro-dark text-astro-text-primary font-sans selection:bg-astro-green selection:text-black" data-id="element-4">
      {activePage !== 'report' && <Navbar activePage={activePage === 'client-vulnerability' ? 'vulnerability' : activePage} onNavigate={page => navigateTo(page as any)} onSignOut={() => setAuthState('logged-out')} orgName={orgName} branding={branding} data-id="element-5" />}

      <main className={activePage !== 'report' ? 'animate-in fade-in duration-300' : ''} data-id="element-6">
        {isFirstLogin ? <WelcomeScreen orgName={orgName} onOrgNameChange={setOrgName} onAddFirstClient={() => {
        setIsFirstLogin(false);
        setIsAddClientOpen(true);
      }} onSkip={() => setIsFirstLogin(false)} data-id="element-7" /> : <>
            {activePage === 'dashboard' && <Dashboard onStartScan={() => setIsScanModalOpen(true)} onViewVulnerabilities={() => navigateTo('vulnerability')} onConfigureScans={() => setIsConfigureOpen(true)} onAddAccount={() => setIsAddAccountOpen(true)} onAddClient={() => setIsAddClientOpen(true)} onManageEnvironment={env => setManagedEnv(env)} onViewClientVulns={handleViewClientVulns} onViewReport={handleViewReport} onOpenReportConfig={handleOpenReportConfig} onViewBulletins={() => navigateTo('bulletins')} data-id="element-8" />}
            {activePage === 'vulnerability' && <VulnerabilityAnalysis onViewClientVulns={handleViewClientVulns} onBack={() => navigateTo('dashboard')} data-id="element-9" />}
            {activePage === 'client-vulnerability' && selectedClientEnv && <ClientVulnerabilityPage environment={selectedClientEnv} onBack={() => navigateTo(previousPage as any)} data-id="element-10" />}
            {activePage === 'report' && selectedClientEnv && <ReportPage environment={selectedClientEnv} selectedSections={reportSections} onBack={() => navigateTo('dashboard')} branding={branding} data-id="element-11" />}
            {activePage === 'settings' && <SettingsPage branding={branding} onBrandingChange={setBranding} data-id="element-12" />}
            {activePage === 'scans' && <ScansPage data-id="element-13" />}
            {activePage === 'bulletins' && <BulletinsPage data-id="element-14" />}
            {activePage === 'teams' && <TeamsPage data-id="element-15" />}
          </>}
      </main>

      {/* Modals */}
      <StartScanModal isOpen={isScanModalOpen} onClose={() => setIsScanModalOpen(false)} data-id="element-16" />

      <ConfigureScansModal isOpen={isConfigureOpen} onClose={() => setIsConfigureOpen(false)} data-id="element-17" />

      <AddClientModal isOpen={isAddClientOpen} onClose={() => setIsAddClientOpen(false)} onClientCreated={() => {
      setIsAddClientOpen(false);
    }} onClientCreatedThenAddAccount={() => {
      setIsAddClientOpen(false);
      setIsAddAccountOpen(true);
    }} orgName={orgName} data-id="element-18" />

      <AddAccountModal isOpen={isAddAccountOpen} onClose={() => setIsAddAccountOpen(false)} data-id="element-19" />

      <ManageEnvironmentModal isOpen={managedEnv !== null} onClose={() => setManagedEnv(null)} environment={managedEnv} data-id="element-20" />

      <ReportConfigModal isOpen={reportConfigEnv !== null} onClose={() => setReportConfigEnv(null)} environment={reportConfigEnv} onGenerate={(env, sections) => {
      setReportConfigEnv(null);
      handleViewReport(env, sections);
    }} data-id="element-21" />
    </div>;
}